// The admin's form for a game feature and its compatibilities (game_features/new and edit, a form marked
// data-game-feature-form), whose lists of characters, series and figures run to thousands of checkboxes.

// Filtering the lists (game_features/_compatibilities): a box that shows only the characters, series and figures
// whose names hold every word typed, and a toggle that shows only the ticked ones, with a series or character
// shrunk to its ticked figures or sub-characters. Both only hide things: a hidden tick still counts.

// Lowercase without accents, so "pokemon" finds Pokémon
function normalize(text) {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

// Each checkbox's label, normalized, by checkbox id: worked out once, the first time the lists are filtered
let labelTexts = null
function labelText(checkbox) {
  if (!labelTexts) {
    labelTexts = new Map()
    document.querySelectorAll('.compatibilities label[for]').forEach((label) => {
      labelTexts.set(label.htmlFor, normalize(label.textContent))
    })
  }
  return labelTexts.get(checkbox.id) || ''
}

function holdsEvery(text, words) {
  return words.every((word) => text.includes(word))
}

// Shows or hides each item of the lists: a character or series, and within it its sub-characters or figures, which
// match on their own name with their parent's (so "smash link" finds Link in the Super Smash Bros. series)
function filterCompatibilities(lists) {
  const words = normalize(lists.querySelector('[data-compatibility-filter]').value).split(/\s+/).filter(Boolean)
  const tickedOnly = lists.querySelector('[data-ticked-only]').getAttribute('aria-pressed') === 'true'
  let shown = 0

  lists.querySelectorAll('.compatibilities > .columns > div > ul > li').forEach((item) => {
    const checkbox = item.querySelector(':scope > input[type=checkbox]')
    const parentText = labelText(checkbox)
    let anyChildShown = false
    item.querySelectorAll(':scope > ul > li').forEach((child) => {
      const childCheckbox = child.querySelector('input[type=checkbox]')
      const show = holdsEvery(parentText + ' ' + labelText(childCheckbox), words) && (!tickedOnly || childCheckbox.checked)
      child.classList.toggle('filtered-out', !show)
      if (show) { anyChildShown = true; shown++ }
    })
    const showItself = holdsEvery(parentText, words) && (!tickedOnly || checkbox.checked)
    item.classList.toggle('filtered-out', !(showItself || anyChildShown))
    if (showItself) shown++
  })

  // A group (e.g. "with only cards") with nothing left to show is hidden too
  lists.querySelectorAll('.compatibilities > .columns > div').forEach((group) => {
    group.classList.toggle('filtered-out', !group.querySelector(':scope > ul > li:not(.filtered-out)'))
  })

  const status = lists.querySelector('[data-compatibility-filter-status]')
  if (words.length === 0 && !tickedOnly) {
    status.textContent = ''
  } else if (shown === 0) {
    status.textContent = tickedOnly && words.length === 0 ? 'Nothing ticked' : 'No matches'
  } else {
    status.textContent = `${shown.toLocaleString()} shown`
  }
}

document.addEventListener('input', (e) => {
  if (!e.target.matches || !e.target.matches('[data-compatibility-filter]')) return
  filterCompatibilities(e.target.closest('[data-compatibility-lists]'))
})

document.addEventListener('keydown', (e) => {
  if (!e.target.matches || !e.target.matches('[data-compatibility-filter]')) return
  // Enter would submit the whole form
  if (e.key === 'Enter') e.preventDefault()
  // Escape clears the filter (as a search box does), showing everything again
  if (e.key === 'Escape' && e.target.value !== '') {
    e.preventDefault()
    e.target.value = ''
    filterCompatibilities(e.target.closest('[data-compatibility-lists]'))
  }
})

// Ticking or unticking a box while showing only the ticked ones doesn't hide it straight away, so a slip can be undone
document.addEventListener('click', (e) => {
  const toggle = e.target.closest && e.target.closest('[data-ticked-only]')
  if (!toggle) return
  const pressed = toggle.getAttribute('aria-pressed') !== 'true'
  toggle.setAttribute('aria-pressed', pressed ? 'true' : 'false')
  toggle.classList.toggle('secondary', !pressed)
  filterCompatibilities(toggle.closest('[data-compatibility-lists]'))
})

// Works with any amiibo: while it's ticked, the lists are greyed out and disabled, so they aren't sent (saving clears
// them, as such a feature lists none), but keep their ticks for if it's unticked again
function worksWithAnyCheckbox(form) {
  return form.querySelector('input[type=checkbox][name="game_feature[works_with_any]"]')
}

function showWorksWithAny(form) {
  const checkbox = worksWithAnyCheckbox(form)
  const lists = form.querySelector('[data-compatibility-lists]')
  if (!checkbox || !lists) return
  lists.disabled = checkbox.checked
  lists.querySelector('[data-works-with-any-note]').hidden = !checkbox.checked
}

document.addEventListener('change', (e) => {
  const form = e.target.closest && e.target.closest('form[data-game-feature-form]')
  if (form && e.target === worksWithAnyCheckbox(form)) showWorksWithAny(form)
})

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('form[data-game-feature-form]').forEach(showWorksWithAny)

  document.querySelectorAll('form[data-game-feature-form] [data-compatibility-tools]').forEach((tools) => {
    tools.hidden = false
    // A filter the browser kept from before (going back to the page) applies again
    if (tools.querySelector('[data-compatibility-filter]').value) filterCompatibilities(tools.closest('[data-compatibility-lists]'))
  })
})

// "Figures this covers" on the edit form (game_features/_covered_figures) follows the boxes as they're ticked: a
// moment after a change to what's covered, the form is sent (saving nothing) to get the section for what's ticked,
// with its Except boxes as they are, which replaces the one on the page. Only the latest answer is shown.
const PREVIEW_DELAY = 250 // ms
const previews = new WeakMap() // form => { timer, controller, sequence }

function preview(form) {
  if (!previews.has(form)) previews.set(form, { timer: null, controller: null, sequence: 0 })
  return previews.get(form)
}

function schedulePreview(form) {
  const state = preview(form)
  clearTimeout(state.timer)
  state.timer = setTimeout(() => fetchPreview(form), PREVIEW_DELAY)
}

async function fetchPreview(form) {
  const state = preview(form)
  if (state.controller) state.controller.abort()
  const controller = state.controller = new AbortController()
  const sequence = ++state.sequence

  const section = form.querySelector('#covered-figures')
  const status = form.querySelector('[data-covered-figures-status]')
  section.setAttribute('aria-busy', 'true')
  section.classList.add('updating')

  // What a submit would send, but as a POST to the preview, not the form's PUT. The form's own token only does for
  // its own action (per-form tokens), so the page's goes in the header.
  const data = new FormData(form)
  data.delete('_method')
  data.delete('authenticity_token')
  const token = document.querySelector('meta[name="csrf-token"]')

  try {
    const response = await fetch(form.dataset.coveredFiguresUrl, {
      method: 'POST',
      body: data,
      headers: { Accept: 'text/html', 'X-CSRF-Token': token ? token.content : '' },
      credentials: 'same-origin',
      signal: controller.signal,
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const html = await response.text()
    if (sequence !== state.sequence) return

    const template = document.createElement('template')
    template.innerHTML = html.trim()
    const replacement = template.content.querySelector('#covered-figures')
    if (!replacement) throw new Error('no section in the answer')

    // Keep the focus on the box it was on, if it's still there
    const focusedId = section.contains(document.activeElement) ? document.activeElement.id : null
    section.replaceWith(replacement)
    if (focusedId) {
      const focused = document.getElementById(focusedId)
      if (focused) focused.focus()
    }
    const count = replacement.querySelectorAll('.covered-group li').length
    status.textContent = `Figures this covers updated: ${count.toLocaleString()} ${count === 1 ? 'figure' : 'figures'}`
  } catch (error) {
    if (error.name === 'AbortError' || sequence !== state.sequence) return
    section.removeAttribute('aria-busy')
    section.classList.remove('updating')
    status.textContent = "Couldn't update Figures this covers, which shows what was ticked before"
    showPreviewError(section)
  } finally {
    if (sequence === state.sequence) state.controller = null
  }
}

function showPreviewError(section) {
  let note = section.querySelector('.covered-figures-error')
  if (!note) {
    note = document.createElement('p')
    note.className = 'covered-figures-error'
    section.querySelector('h2').after(note)
  }
  note.textContent = "Couldn't update this to match the boxes above (it shows what was ticked before). Submitting still saves what's ticked."
}

document.addEventListener('change', (e) => {
  const form = e.target.closest && e.target.closest('form[data-game-feature-form][data-covered-figures-url]')
  if (!form) return

  if (e.target.closest('#covered-figures')) {
    // An Except box doesn't change what's covered, but an answer already on its way was made without this change,
    // and would undo it: drop that one and ask again
    const state = preview(form)
    if (state.controller) {
      state.controller.abort()
      state.controller = null
      schedulePreview(form)
    }
  } else if ((e.target.type === 'checkbox' && e.target.closest('[data-compatibility-lists]')) || // not the filter box
             e.target === worksWithAnyCheckbox(form) ||
             e.target.name === 'game_feature[excludes_figure_type]') {
    schedulePreview(form)
  }
})
